import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTemplateById } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import PreviewTemplate from './PreviewTemplate';
import { editTemplates } from '../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import Loader from '../UI/Loader/Loader';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { isValidJsonString } from '../../utils/helper';

const EditTemplate = () => {
    const { id } = useParams();
    const [template, setTemplate] = useState({});
    const [loading, setLoading] = useState(false);
    const { userData } = useUserContext();
    const navigator = useNavigate();
    const { enums, content } = useContentfulContext();

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [cssCode, setCssCode] = useState("");
    const [htmlCode, setHtmlCode] = useState("");
    const [jsonSchemaCode, setJsonSchemaCode] = useState("");
    const [configJsonCode, setConfigJsonCode] = useState("");
    const [templateName, setTemplateName] = useState('');
    const [templateIconURL, setTemplateIconURL] = useState({ web_only: "", mobile_only: "" });
    const [templateViewURL, setTemplateViewURL] = useState({ portrait_only: "", landscape_only: "" });
    const [jsonBSchemaCode, setJsonBSchemaCode] = useState("");
    const [templateType, setTemplateType] = useState("");
    const [templatePlatform, setTemplatePlatform] = useState("");
    const [isLandscapeSupport, setIsLandscapeSupport] = useState(true);
    const [templateSource, setTemplateSource] = useState("");
    const [apiEndPoint, setApiEndPoint] = useState("");
    const [defaultDevice, setDefaultDevice] = useState("");

    const getTemplateByIdHandler = async (id) => {
        try {
          setLoading(true);
          const result = await getTemplateById(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            id,
            userData?.defaultAEMAdobeIO?._id
          );
    
          setTemplate(result.data);
          setErrorMessage('');
        } catch (error) {
          toast.error(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
        } finally {
          setLoading(false);
        }
    };

    useEffect(() => {
        getTemplateByIdHandler(id);
    }, []);

    useEffect(() => {
        if (Object.keys(template).length > 0) {
            setCssCode(() => {
              return template?.css || "";
            });
            setHtmlCode(() => {
              return template?.html || "";
            });
            setConfigJsonCode(() => {
              return template?.config_json || "";
            });
            setJsonSchemaCode(() => {
              return template?.json_schema || "";
            });
            setTemplateName(() => {
                return template?.name || "";
            })
            setTemplateIconURL({ web_only: template?.icon_url?.web_only || "", mobile_only: template?.icon_url?.mobile_only || ""});
            setTemplateViewURL({ portrait_only: template?.view_url?.portrait_only || "", landscape_only: template?.view_url?.landscape_only || ""});
            setTemplateType(template?.type || "")
            setTemplatePlatform(template?.platform)
            setIsLandscapeSupport(template?.landscape_support)
            setTemplateSource(template?.source || "");
            setApiEndPoint(template?.api_end_point || "");
        }
    }, [template]);

    useEffect(() => {
      if (template?.default_device) {
        setDefaultDevice(template.default_device);
      }
    }, [template]);

    const validateURL = () => {
      const trimmedURL = apiEndPoint.trim();
      try {
          new URL(trimmedURL);
          setErrorMessage("");
          return true;
        } catch (e) {
          setErrorMessage(content.MANAGE_TEMPLATE_CONTENT.invalid_url);
          return false;
      }
    }

    const validateTemplateName = () => {
        if (!templateName.trim()) {
            setErrorMessage(content.MANAGE_TEMPLATE_CONTENT.template_name);
        } else {
            setErrorMessage("");
        }
    };

    const editTemplateHandler = async () => {
        try {
          setLoading(true);

          const payload = {
            name: templateName,
            icon_url: templateIconURL,
            view_url: {
              portrait_only: templateViewURL.portrait_only,
              landscape_only: templatePlatform === enums?.templatePlatforms?.web_only?.id ? "" : templateViewURL.landscape_only,
            },
            html: htmlCode,
            css: cssCode,
            type: templateType,
            platform: templatePlatform,
            landscape_support: isLandscapeSupport,
            source: templateSource,
            api_end_point : apiEndPoint,
            config_json: configJsonCode,
            json_schema: templateSource == enums?.templateSources.api.id ? "{}" : jsonSchemaCode,
            default_device: defaultDevice,
          };
          const result = await editTemplates(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            id,
            payload,
            undefined,
            {
              event_name: ANALYTICS_EVENTS.UPDATE_TEMPLATE,
              metadata: { template_id: id, template_name: templateName, template_type: templateType }
            }
          );
          setTemplate(result.data);
          setErrorMessage("");
          setSuccessMessage(content.MANAGE_TEMPLATE_CONTENT.edit_success);
        } catch (error) {
          setErrorMessage(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
          setLoading(false);
        }
    };

    const saveTemplateHandler = () => {
        const isValid = validateFields();
        if(!isValid) return;
        if (!errorMessage) {
            editTemplateHandler();
        }
    };

    const validateFields = () => {
      let names = [];
      if(!templateName?.trim()) names.push('Name');
      if(templatePlatform?.trim() == enums?.templatePlatforms.web_only.id && !templateIconURL?.web_only?.trim()) {
        names.push("Web Icon URL");
      }
      if(templatePlatform?.trim() == enums?.templatePlatforms.mobile_only.id && !templateIconURL?.mobile_only?.trim()) {
        names.push("Mobile Icon URL");
      }
      if(templatePlatform == enums?.templatePlatforms.responsive.id && (!templateIconURL?.web_only?.trim() || !templateIconURL?.mobile_only?.trim())) {
        names.push('Web & Mobile Icons URL');
      }
      if(!templatePlatform.trim()) names.push(content.MANAGE_TEMPLATE_CONTENT.platform);
      if(!templateSource.trim()) names.push(content.MANAGE_TEMPLATE_CONTENT.source);
      if(templateSource == enums?.templateSources.api.id && !apiEndPoint.trim()) names.push('API End Point');
      if(names.length > 0) {
        const suffix = names.length > 1 ? ' are required' : ' is required';
        setErrorMessage(names.join(', ') + suffix);
        return false;
      }
  
      const isValidJsonSchema = isValidJsonString(templateSource == enums?.templateSources.api.id ? "{}" : jsonSchemaCode);
      if(!isValidJsonSchema) {
        setErrorMessage(content.MANAGE_TEMPLATE_CONTENT.invalid_json_schema);
      }
  
      const isValidJsonConfigJson = isValidJsonString(configJsonCode);
      if(!isValidJsonConfigJson) {
        setErrorMessage(content.MANAGE_TEMPLATE_CONTENT.invalid_config_json);
      }
  
      if(!isValidJsonSchema || !isValidJsonConfigJson) return false;
  
      setErrorMessage('');
      return true;
    }

    return (
        <div className='h-full'>
            {loading && <Loader />}
            {successMessage && (
                <SuccessAlert disappearTime={5000}>{successMessage}</SuccessAlert>
            )}
            <div class="flex flex-col w-full h-full px-4 md:px-8"> 
                {Object.keys(template).length > 0 && <PreviewTemplate
                    templateName={templateName}
                    setTemplateName={setTemplateName}
                    templateIconURL={templateIconURL}
                    setTemplateIconURL={setTemplateIconURL}
                    templateViewURL={templateViewURL}
                    setTemplateViewURL={setTemplateViewURL}
                    cssCode={cssCode}
                    htmlCode={htmlCode}
                    setHtmlCode={setHtmlCode}
                    setCssCode={setCssCode}
                    configJsonCode={configJsonCode}
                    setConfigJsonCode={setConfigJsonCode}
                    selectedTemplate={template}
                    validateTemplateName={validateTemplateName}
                    submitError={errorMessage}
                    setSubmitError={setErrorMessage}
                    saveTemplateHandler={saveTemplateHandler}
                    selectedTemplateId={id}
                    jsonSchemaCode={jsonSchemaCode}
                    setJsonSchemaCode={setJsonSchemaCode}
                    jsonBSchemaCode={jsonBSchemaCode}
                    setJsonBSchemaCode={setJsonBSchemaCode}
                    templateType={templateType}
                    templatePlatform={templatePlatform}
                    setTemplatePlatform={setTemplatePlatform}
                    isLandscapeSupport={isLandscapeSupport}
                    setIsLandscapeSupport={setIsLandscapeSupport}
                    templateSource={templateSource}
                    setTemplateSource={setTemplateSource}
                    apiEndPoint={apiEndPoint}
                    setApiEndPoint={setApiEndPoint}
                    validateURL={validateURL}
                    setTemplateType={setTemplateType}
                    defaultDevice={defaultDevice}
                    setDefaultDevice={setDefaultDevice}
                />}
            </div>
        </div>
        // <CreateTemplate />
    )
}

export default EditTemplate;